import React, { createContext, useContext, ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';

interface LanguageContextType {
    currentLanguage: string;
    changeLanguage: (language: string) => Promise<void>;
    t: (key: string, options?: any) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
    children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
    const { t, i18n } = useTranslation();

    const changeLanguage = async (language: string) => {
        await i18n.changeLanguage(language);
    };

    // Sync HTML lang attribute and dayjs locale with current language
    useEffect(() => {
        const lang = i18n.language || 'en';
        const isZh = lang.startsWith('zh');
        const isKh = lang.startsWith('kh');
        
        if (typeof document !== 'undefined') {
            if (isKh) {
                document.documentElement.lang = 'km'; // ISO 639-1 code for Khmer
            } else if (isZh) {
                document.documentElement.lang = 'zh';
            } else {
                document.documentElement.lang = 'en';
            }
        }
        
        // dayjs doesn't have Khmer locale, fallback to English
        if (isZh) {
            dayjs.locale('zh-cn');
        } else {
            dayjs.locale('en');
        }
    }, [i18n.language]);

    const value: LanguageContextType = {
        currentLanguage: i18n.language,
        changeLanguage,
        t,
    };

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = (): LanguageContextType => {
    const context = useContext(LanguageContext);
    if (context === undefined) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};
